import axios from "axios";
import { Room } from "livekit-client";
import { getAuthHeader, getValidAccessToken, refreshAccessToken } from "../../../utils/authRefresh";

export enum LiveKitActionMessageType {
  RequestAssessmentTime = "request_assessment_time",
  TerminateAssessment = "terminate_assessment",
  RequestAssessmentQuestionIndex = "request_assessment_question_index",
}

export const sendLiveKitMessage = async (action: LiveKitActionMessageType, room: Room, assessmentId: string) => {
  if (!room) {
    return;
  }

  const message = {
    action,
    assessmentId,
  };

  room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });
};

export const fetchLiveKitToken = async (
  assessmentId: string,
  onTokenFetched: (token: string) => void,
  onError: () => void
) => {
  const authHeader = await getAuthHeader();
  if (!authHeader.Authorization) {
    onError();
    return;
  }
  try {
    const data = await axios.post(
      "/api/livekit/token",
      {
        assessmentId,
      },
      {
        headers: authHeader,
      }
    );
    onTokenFetched(data.data.token);
  } catch (error) {
    const status = (error as any)?.response?.status;
    if (status === 401 || status === 403) {
      const refreshed = await refreshAccessToken();
      if (refreshed?.accessToken) {
        try {
          const retry = await axios.post(
            "/api/livekit/token",
            {
              assessmentId,
            },
            {
              headers: {
                Authorization: `Bearer ${refreshed.accessToken}`,
              },
            }
          );
          onTokenFetched(retry.data.token);
          return;
        } catch (retryError) {
          console.error("Error fetching LiveKit token after refresh:", retryError);
        }
      }
    }
    console.error("Error fetching LiveKit token:", error);
    onError();
  }
};

export const startSseAssessment = async (
  assessmentId: string,
  onError: () => void
): Promise<
  { startedAt: string; durationSec: number; remainingSec?: number; introText?: string; introAudio?: string } | undefined
> => {
  const authHeader = await getAuthHeader();
  if (!authHeader.Authorization) {
    onError();
    return undefined;
  }
  try {
    const data = await axios.post(
      "/api/sse-assessment/start-assessment",
      { assessmentId },
      {
        headers: authHeader,
      }
    );
    return {
      startedAt: data.data.startedAt,
      durationSec: data.data.durationSec,
      remainingSec: data.data.remainingSec,
      introText: data.data.introText,
      introAudio: data.data.introAudio,
    };
  } catch (error) {
    const status = (error as any)?.response?.status;
    if (status === 401 || status === 403) {
      const refreshed = await refreshAccessToken();
      if (refreshed?.accessToken) {
        try {
          const retry = await axios.post(
            "/api/sse-assessment/start-assessment",
            { assessmentId },
            {
              headers: {
                Authorization: `Bearer ${refreshed.accessToken}`,
              },
            }
          );
          return {
            startedAt: retry.data.startedAt,
            durationSec: retry.data.durationSec,
            remainingSec: retry.data.remainingSec,
            introText: retry.data.introText,
            introAudio: retry.data.introAudio,
          };
        } catch (retryError) {
          console.error("Error starting SSE assessment after refresh:", retryError);
        }
      }
    }
    console.error("Error starting SSE assessment:", error);
    onError();
    return undefined;
  }
};

export const getSseAssessmentTimer = async (): Promise<{
  startTime: number;
  durationSec: number;
  remainingSec: number;
  questionIndex?: number;
  introPlayed?: boolean;
} | null> => {
  const authHeader = await getAuthHeader();
  if (!authHeader.Authorization) {
    return null;
  }
  try {
    const data = await axios.get("/api/sse-assessment/timer", {
      headers: authHeader,
    });
    return data.data;
  } catch (error) {
    const status = (error as any)?.response?.status;
    if (status === 401 || status === 403) {
      const refreshed = await refreshAccessToken();
      if (refreshed?.accessToken) {
        try {
          const retry = await axios.get("/api/sse-assessment/timer", {
            headers: {
              Authorization: `Bearer ${refreshed.accessToken}`,
            },
          });
          return retry.data;
        } catch (retryError) {
          console.error("Error getting SSE assessment timer after refresh:", retryError);
        }
      }
    }
    console.error("Error getting SSE assessment timer:", error);
    return null;
  }
};

export const endSseAssessment = async (onError: () => void): Promise<void> => {
  const authHeader = await getAuthHeader();
  if (!authHeader.Authorization) {
    onError();
    return;
  }
  try {
    await axios.post(
      "/api/sse-assessment/end-assessment",
      {},
      {
        headers: authHeader,
      }
    );
  } catch (error) {
    const status = (error as any)?.response?.status;
    if (status === 401 || status === 403) {
      const refreshed = await refreshAccessToken();
      if (refreshed?.accessToken) {
        try {
          await axios.post(
            "/api/sse-assessment/end-assessment",
            {},
            {
              headers: {
                Authorization: `Bearer ${refreshed.accessToken}`,
              },
            }
          );
          return;
        } catch (retryError) {
          console.error("Error ending SSE assessment after refresh:", retryError);
        }
      }
    }
    console.error("Error ending SSE assessment:", error);
    onError();
  }
};

export const continueSseAssessment = async (
  assessmentId: string,
  audio: string,
  onError: () => void,
  onChunk?: (chunk: { seq: number; text: string; audio: string }) => void,
  onStateUpdate?: (data: { questionIndex: number; introPlayed: boolean }) => void,
  onDone?: () => void
): Promise<{ success: boolean; questionIndex?: number; completed?: boolean }> => {
  const token = await getValidAccessToken({ minTtlSeconds: 30 });
  if (!token) {
    onError();
    throw new Error("Missing access token");
  }
  try {
    let response = await fetch("/api/sse-assessment/continue-assessment", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ assessmentId, audio }),
    });

    if (response.status === 401 || response.status === 403) {
      const refreshed = await refreshAccessToken();
      if (refreshed?.accessToken) {
        response = await fetch("/api/sse-assessment/continue-assessment", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${refreshed.accessToken}`,
          },
          body: JSON.stringify({ assessmentId, audio }),
        });
      }
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Handle SSE stream
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    if (!reader) {
      throw new Error("No response body reader available");
    }

    let result: { success: boolean; questionIndex?: number; completed?: boolean } = {
      success: false,
    };

    let currentEventType = "";
    let currentData = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split("\n\n");
      buffer = chunks.pop() || "";

      for (const chunk of chunks) {
        const lines = chunk.split("\n");
        currentEventType = "";
        currentData = "";

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEventType = line.substring(7).trim();
          } else if (line.startsWith("data: ")) {
            currentData = line.substring(6).trim();
          }
        }

        if (currentEventType && currentData) {
          try {
            const parsed = JSON.parse(currentData);
            if (currentEventType === "chunk" && onChunk) {
              onChunk(parsed);
            } else if (currentEventType === "state_update" && onStateUpdate) {
              onStateUpdate(parsed);
              result.questionIndex = parsed.questionIndex;
            } else if (currentEventType === "done" && onDone) {
              onDone();
            } else if (currentEventType === "completed") {
              result.completed = parsed.completed || true;
            }
          } catch (e) {
            // Not JSON, ignore
            console.error("Error parsing SSE data:", e);
          }
        }
      }
    }

    // Process any remaining buffer
    if (buffer.trim()) {
      const lines = buffer.split("\n");
      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEventType = line.substring(7).trim();
        } else if (line.startsWith("data: ")) {
          currentData = line.substring(6).trim();
          if (currentEventType && currentData) {
            try {
              const parsed = JSON.parse(currentData);
              if (currentEventType === "chunk" && onChunk) {
                onChunk(parsed);
              } else if (currentEventType === "state_update" && onStateUpdate) {
                onStateUpdate(parsed);
                result.questionIndex = parsed.questionIndex;
              } else if (currentEventType === "done" && onDone) {
                onDone();
              } else if (currentEventType === "completed") {
                result.completed = parsed.completed || true;
              }
            } catch (e) {
              console.error("Error parsing SSE data:", e);
            }
          }
        }
      }
    }

    result.success = true;
    return result;
  } catch (error) {
    console.error("Error continuing SSE assessment:", error);
    onError();
    throw error;
  }
};
